import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import client from 'api/client';
import { errorMessage } from 'helpers/utils';
import { useParams } from 'react-router-dom';
import { useIsPublicPage } from 'hooks/use-is-public-page';
import { GET_SEARCH_DATA, GET_SEARCH_PUBLIC_DATA } from './constant';

export type NodeProperty = {
  id: string;
  label: string;
  color: string;
  [name: string]: string;
};

export type EdgeType = {
  id: string;
  relation: string;
  source_id: string;
  target_id: string;
  source_name: null;
  target_name: null;
  source_color: null;
  target_color: null;
};
export type CommentsType = {
  id: string;
  project_id: string;
  color: string;
  default_image?: string;
  icon?: string;
  title?: string;
  node_id: string;
  privacy: 'private' | 'public';
  name?: string;
  node_type?: string;
  comments: string;
};

export type EdgeProperties = {
  relation: string;
  id: string;
  source_id: string;
  target_id: string;
  source_name: string;
  source_color: string;
  target_name: string;
  target_color: string;
  [name: string]: string;
};

type ProjectResponse = {
  comments: CommentsType[];
  edgeTypes: EdgeType[];
  edgeProperties: EdgeProperties[];
  nodeTypes: [
    {
      id: string;
      label: string;
      color: string;
    }
  ];
  nodeProperties: NodeProperty[];
};

type GetProjectParam = {
  id?: string;
  projectId: string;
};

type QueryKey = Omit<GetProjectParam, 'url'> | string;

export type GetNeo4jData = {
  data: ProjectResponse;
};

type QueryResponse = {
  data: GetNeo4jData;
};

type Options = UseQueryOptions<QueryResponse, Error, GetNeo4jData, QueryKey[]>;

type Result = {
  isInitialLoading: boolean;
} & GetNeo4jData;

export const useGetSearchData = (options: Options = { enabled: false }, search: string): Result => {
  const params = useParams();

  const isPublicPage = useIsPublicPage();

  const urlSearch = isPublicPage ? GET_SEARCH_PUBLIC_DATA : GET_SEARCH_DATA;

  const url = urlSearch.replace(':project_id', params?.id || '');

  const result = useQuery({
    queryKey: [url, search],
    queryFn: () => client.get(url, { params: { search } }),
    ...options,
    onError: errorMessage,
  });

  const { data, isInitialLoading } = result;

  return { isInitialLoading, data: data?.data ?? ({} as ProjectResponse) };
};
